/**
 * English UI catalogue of the motion columns (W2-04a motion-columns), namespace 'motion'.
 * Imports only src/i18n/define.ts; zh-CN lives in src/i18n/zh-CN/motion.ts (W3-09).
 * Vocabulary (DESIGN.md §2.5): UI strings say "keyframe"; the constant segment is "Stay, then jump".
 */

import { catalogOf, defineMessages } from '../../i18n/define';

export const m = defineMessages('motion', {
  // Column and property names
  columnTitle: 'Motion columns',
  propMoveX: 'Position X',
  propMoveY: 'Position Y',
  propPosition: 'Position',
  propRotate: 'Rotation',
  propAlpha: 'Opacity',
  propSpeed: 'Scroll speed',
  propLength: 'Length',
  propThickness: 'Thickness',
  propSizeX: 'Size X',
  propSizeY: 'Size Y',
  propSize: 'Size',
  propColor: 'Colour',
  propText: 'Text',
  propIncline: 'Note tilt',
  propPaint: 'Brush',
  propGif: 'GIF progress',
  propScaleXAny: 'Length / Size X',
  propScaleYAny: 'Thickness / Size Y',
  shortMoveX: 'Pos X',
  shortMoveY: 'Pos Y',
  shortPosition: 'Position',
  shortRotate: 'Rotate',
  shortAlpha: 'Opacity',
  shortSpeed: 'Speed',
  shortLength: 'Length',
  shortThickness: 'Thick',
  shortSizeX: 'Size X',
  shortSizeY: 'Size Y',
  shortSize: 'Size',
  shortColor: 'Colour',
  shortText: 'Text',
  shortIncline: 'Tilt',
  shortPaint: 'Brush',
  shortGif: 'GIF',
  // Narrow columns (below about 84 px, UX-034): the shortest names; the tooltip and the accessible name stay full.
  tinyMoveX: 'X',
  tinyMoveY: 'Y',
  tinyPosition: 'XY',
  tinyRotate: 'Rot',
  tinyAlpha: 'Op',
  tinySpeed: 'Spd',
  tinyLength: 'Len',
  tinyThickness: 'Thk',
  tinySizeX: 'W',
  tinySizeY: 'H',
  tinySize: 'WH',
  tinyColor: 'Col',
  tinyText: 'Txt',
  tinyIncline: 'Tilt',
  tinyPaint: 'Brush',
  tinyGif: 'GIF',
  subX: 'X',
  subY: 'Y',
  subW: 'W',
  subH: 'H',
  subPair: '{a} | {b}',
  columnLabel: '{prop}, {line}',
  columnLabelLayer: '{prop}, layer {layer}, {line}',
  rpeTermMoveX: 'RPE: moveXEvents',
  rpeTermMoveY: 'RPE: moveYEvents',
  rpeTermRotate: 'RPE: rotateEvents',
  rpeTermAlpha: 'RPE: alphaEvents',
  rpeTermSpeed: 'RPE: speedEvents · 流速',
  rpeTermScaleX: 'RPE: scaleXEvents',
  rpeTermScaleY: 'RPE: scaleYEvents',
  rpeTermColor: 'RPE: colorEvents',
  rpeTermText: 'RPE: textEvents',
  rpeTermIncline: 'RPE: inclineEvents · 倾斜',
  rpeTermPaint: 'RPE: paintEvents',
  rpeTermGif: 'RPE: gifEvents',

  // Header
  headerNow: 'now {value}',
  headerNowPair: 'now {a} | {b}',
  headerSigma: 'Σ {value}',
  headerNotSet: 'not set',
  headerCrosses: 'crosses in {sec} s',
  headerCrossesNever: 'notes do not move',
  headerFocused: 'Focused: K adds keyframes here',
  headerFocusHint: 'Click to focus this column (K, G and Ctrl+A act on it)',
  headerMenu: 'Column options',
  headerPinnedFrom: 'Pinned from {line}',
  headerUnpin: 'Unpin this column',
  headerLinkOn:
    'X and Y are linked: keyframes on one axis are added, moved and deleted on the other too. Click to unlink.',
  headerLinkOff: 'X and Y are not linked. Click to link them (keyframes on one axis then follow on the other).',
  headerLinkLabelOn: 'X and Y linked',
  headerLinkLabelOff: 'X and Y not linked',
  headerUsesLayers: 'uses {n} layers',
  headerUsesLayersShort: '{n}',
  layerChipsMenu: 'Layer {n} of {count}: choose another layer',
  layerChipCompact: 'L{n}/{count}',
  headerUsesLayersHint:
    'This motion adds up {n} layers. The curve shows their sum; edits go to the base layer. Click to see the layers (Full level).',
  headerInUse: 'in use',
  headerInUseHint: 'Shown because this line uses it (a Full-level property)',
  headerSpeedBaked: 'baked on export',
  headerSpeedBakedHint: 'Curved scroll speed is turned into straight pieces on export (Classic speed mode).',
  headerSpeed170: '1.7 speed',
  headerSpeed170Legacy: '1.7 speed (RPE < 1.7)',
  headerSpeed170Hint: 'RPE 1.7 speed curves: the speed follows the drawn curve.',
  headerSpeed170LegacyHint:
    'RPE 1.7 speed curves with RPEVersion below 170: Phira integrates the curve the old way (speed = k·f′(x) + b). Change it in Project settings.',
  headerExpand: 'Show this column wide',
  headerCompactLabel: '{prop}: show this column wide',
  headerAriaPinned: '{prop}, pinned from {line}',
  headerAriaFocused: '{prop}, focused: K adds keyframes here',
  headerAriaPinnedFocused: '{prop}, pinned from {line}, focused: K adds keyframes here',
  headerTooltip: '{prop} · {rpe} · {hint}',
  headerPreviewOnly: 'muted: preview only',

  // Layer chips
  layerChip: 'L{n}',
  layerChipNamed: '{name}',
  layerChipHas: 'Layer {n}: has keyframes',
  layerChipEmpty: 'Layer {n}: empty',
  layerChipActive: 'Layer {n}: editing',
  layerChipMuted: 'Layer {n}: muted in the preview',
  layerChipSolo: 'Layer {n}: solo in the preview',
  layerChipsLabel: 'Layers of {prop}',
  layerChipAria: '{label}: {state}',
  layerAdd: 'Add a layer',
  layerMenu: 'Layer {n} options',
  layerRename: 'Rename layer…',
  layerRenamePrompt: 'Name of layer {n}',
  layerDelete: 'Delete layer',
  layerMute: 'Mute in the preview',
  layerUnmute: 'Unmute',
  layerSolo: 'Solo in the preview',
  layerUnsolo: 'Stop solo',
  layerMoveHere: 'Move selected segments here',
  layerMutedNote: 'Preview only: the export still has every layer.',

  // Easing badges (Guided plain words, Full ids)
  easeLinear: 'Linear',
  easeHold: 'Stay, then jump',
  easeHoldShort: 'Stay',
  easeBezier: 'Custom curve',
  easeBezierShort: 'Bz',
  easeId: '{id}',
  easeIdSlice: '{id} part',
  easeWords: '{strength}, {shape}',
  easeWordsSlice: '{strength}, {shape} (part)',
  strengthGentle: 'Gentle',
  strengthSoft: 'Soft',
  strengthMedium: 'Medium',
  strengthStrong: 'Strong',
  strengthStronger: 'Stronger',
  strengthExtreme: 'Extreme',
  strengthSharp: 'Sharp',
  strengthOvershoot: 'Overshoot',
  strengthSpringy: 'Springy',
  strengthBouncy: 'Bouncy',
  shapeSlowStart: 'slow start',
  shapeSlowEnd: 'slow end',
  shapeSlowBoth: 'slow both',
  badgeIgnored: 'ignored',
  badgeIgnoredGuided: 'curve ignored',
  badgeBaked: 'baked',
  badgeBakedGuided: 'baked on export',
  badgeApprox: '≈ split',
  badgeApproxGuided: 'split ≈ 0.1 %',
  badgeTextSwitch: 'Switch',
  badgeTextTypeOn: 'Type on',
  badgeTextErase: 'Erase',
  badgeTextExtend: 'Extend',
  badgeTextCounter: 'Counter',
  notesHidden: 'notes hidden (still judged)',
  ghostDefaultWhite: 'Phira default (white)',
  ghostDefaultText: 'Phira default (empty)',
  layerEmpty: 'Layer {n} is empty. Double-click to add a keyframe.',
  lineGone: 'This line no longer exists.',
  rawPadding: 'padding',
  rawSingle: 'single keyframe',
  rawTrailing: 'trailing value',
  rawLead: 'imported lead-in',
  rawBakePiece: 'baked piece',
  rawEasing: 'e{id}',
  rawEasingSlice: 'e{id} [{l}, {r}]',
  rawBezier: 'bezier {p}',
  rawLink: 'link {n}',
  rawValues: '{from} → {to}',

  // Values and lists
  valueText: '"{text}"',
  valueRgb: '[{r}, {g}, {b}]',
  valueNone: '-',
  listJoin: '{a} · {b}',

  // Units
  unitPx: '{v}',
  unitDeg: '{v}°',
  unitPercent: '{v}%',
  unitSpeed: '{v}',
  unitThickness: '×{v}',
  unitRaw: '{v}',

  // Placement (RPE preset)
  placeStart: 'Segment: press R again for the end (a digit = curve), Esc cancels',
  placeStartHold: 'Stay segment: press R for the end, Esc cancels',
  placeBlocked: 'Keyframes in between: choose a free stretch',
  placeTooShort: 'The end must be after the start',
  placeNoColumn: 'Point at a motion column first (R and Q place segments there)',
  placeHintEnd: 'end the segment',
  placeHintDigit: 'curve by number (two digits within 0.4 s)',
  placeHintCancel: 'cancel',

  // Drag hints
  dragHintLock: 'lock to one axis',
  dragHintNoSnap: 'no snap',
  dragHintRound: 'round values',
  dragHintCancel: 'cancel',
  dragReadout: '{beat} · {value}',

  // Commands
  cmdAddKey: 'Add keyframe at the playhead',
  cmdAddKeyHint: '{keys} adds a keyframe at the playhead on the focused column',
  cmdAddKeyAll: 'Add keyframes on every animated property',
  cmdAddKeyAllHint: '{keys} adds a keyframe to every animated property of the line at the playhead',
  cmdAddKeyHere: 'Add keyframe here',
  cmdDeleteKeys: 'Delete keyframes',
  cmdDeleteKeysHint: '{keys} deletes the selected keyframes',
  cmdNudgeValueUp: 'Increase keyframe values',
  cmdNudgeValueDown: 'Decrease keyframe values',
  cmdNudgeTimeLater: 'Move keyframes later',
  cmdNudgeTimeEarlier: 'Move keyframes earlier',
  cmdNudgeValueUpHint: '{keys} nudge the values of the selected keyframes (Shift: 10 steps)',
  cmdSelectColumn: 'Select all keyframes of this column',
  cmdResetProperty: 'Reset property',
  cmdSplitAtPlayhead: 'Split at the playhead',
  cmdHold: 'Stay, then jump',
  cmdHoldHint: '{keys} toggles Stay, then jump on the selected segments',
  cmdDeleteSegments: 'Delete segments',
  cmdDeleteSegmentsHint: '{keys} deletes the selected segments (the line stays at their start value)',
  cmdSelectSegmentKeys: 'Select the keyframes of the segments',
  toastSegmentsDeleted:
    '{count, plural, one {Deleted # segment} other {Deleted # segments}}: the line stays at the start value{trimmed, plural, =0 {} other { (the last keyframe went)}}',
  toastSegmentsAlreadyStay:
    '{count, plural, one {This segment already stays at its start value} other {These segments already stay at their start value}}. Select keyframes to delete them.',
  txDeleteSegments: 'Delete {count, plural, one {# segment} other {# segments}}',
  cmdJump: 'Make jump / Continue from previous',
  cmdJumpHint: '{keys} makes a jump, or continues from the previous value',
  cmdNegate: 'Negate values',
  cmdNegateHint: '{keys} negates the selected values (opacity: 0 %)',
  cmdOpacityFull: 'Opacity 100 %',
  cmdOpacityFullHint: '{keys} sets the selected opacity keyframes to 100 %',
  cmdEasing: 'Change curve…',
  cmdEasingHint: '{keys} opens the curve picker for the selected segments',
  cmdLayerAdd: 'Add layer',
  cmdLayerDelete: 'Delete layer',
  cmdLayerRename: 'Rename layer',
  cmdLayerSetActive: 'Edit layer…',
  cmdLayerMoveSelection: 'Move segments to layer…',
  cmdLayerMute: 'Mute layer in the preview',
  cmdLayerSolo: 'Solo layer in the preview',
  cmdLayerUnmuteAll: 'Unmute all layers',
  cmdToggleLink: 'Link Position X and Y',
  cmdAddProperty: 'Add property…',
  cmdToggleSigma: 'Show the sum of layers (Σ)',
  cmdLockRange: 'Lock the value range',
  cmdShowRaw: 'Show raw events',
  cmdCopyRaw: 'Copy raw events as RPE JSON',
  cmdCopyCurve: 'Copy curve',
  cmdPasteCurve: 'Paste curve',
  cmdTemplates: 'Motion templates…',
  cmdClearPinned: 'Unpin all columns',
  cmdPlaceSegment: 'Place segment (R … R)',
  cmdPlaceSegmentHint: '{keys} over a motion column starts a segment; press it again for the end',
  cmdPlaceHold: 'Place Stay segment (Q … R)',
  cmdPlaceDigit: 'Segment curve by number',
  cmdPlaceCancel: 'Cancel segment placement',
  cmdFineTune: 'Fine-tune values',
  cmdFocusColumn: 'Focus column…',
  cmdPinColumn: 'Pin column',
  cmdUnpinColumn: 'Unpin column',
  cmdCompare: 'Compare lines',
  argProp: 'Property',
  argLayer: 'Layer',
  argName: 'Name',
  argDigit: 'Digit',
  argSteps: 'Steps',

  // Reasons and toasts
  needProject: 'Open a project first',
  needFocus: 'Click a motion column first',
  needActiveLine: 'Choose a line first',
  needKeys: 'Select keyframes first',
  needSegments: 'Select segments (the curve between two keyframes) first',
  needTwoKeys: 'No segment here yet: add two keyframes (K at two beats), then click the curve between them',
  needOpacityKeys: 'S sets opacity keyframes to 100 %; the selection has no opacity keyframes',
  needNumeric: 'Only numeric keyframes can jump this way',
  needTwoLines: 'Select two or more lines in the Lines panel first',
  needLayered: 'Only Position, Rotation, Opacity and Scroll speed have layers',
  needFullForLayers: 'Layers are a Full-level feature',
  needXyLine: 'Choose a line first',
  needCurve: 'Copy a curve first',
  needMuted: 'No layer is muted',
  needPinned: 'No columns are pinned',
  needPlacement: 'No segment is being placed',
  layerLimit: 'A property can have at most 4 layers',
  resetDone: 'Reset {prop}. {absent}',
  linkedOn: 'Position X and Y of {line} are linked',
  linkedOff: 'Position X and Y of {line} are no longer linked',
  curveCopied: 'Curve copied',
  curvePasted: '{count, plural, one {Curve pasted on # segment} other {Curve pasted on # segments}}',
  rawCopied: 'Raw events copied',
  rawCopyFailed: 'The browser did not allow copying',
  bakeAction: 'Bake',
  layerMutedToast: 'Layer {n} of {prop} is muted in the preview. The export is unchanged.',
  layerSoloToast: 'Only layer {n} of {prop} plays in the preview. The export is unchanged.',
  layerUnmutedAll: 'All layers play again',
  layerDeleteConfirmTitle: 'Delete layer {n} of {prop}?',
  layerDeleteConfirmBody:
    'Its {count, plural, one {# keyframe is} other {# keyframes are}} removed. You can undo this.',
  layerDeleteConfirm: 'Delete layer',
  guidedLayersTitle: 'This motion uses {n} layers',
  guidedLayersBody: 'The column shows their sum. Edit the base layer here, or switch to Full to see every layer.',
  guidedLayersBase: 'Edit the base layer',
  guidedLayersFull: 'Switch to Full',
  compareStarted: 'Comparing {prop} on {count, plural, one {# line} other {# lines}}',
  compareClear: 'Unpin',
  pinned: '{prop} of {line} pinned',
  unpinned: '{prop} of {line} unpinned',
  addPropertyTitle: 'Add property',
  addPropertyBody: 'Choose a property to animate on {line}',
  comparePropTitle: 'Compare which property?',
  focusPropTitle: 'Focus which column?',
  focusAddOption: 'Add {prop}…',
  focusUnavailable: '{prop} cannot be animated on {line}',
  lineTypeNeeded: 'This property changes the line type. Use Add property to change it first.',
  lineTypeSkipped:
    '{prop} was not added to {count, plural, one {# line} other {# lines}}: it would change the line type. Use Add property there first.',
  templatesMulti: 'Templates were applied to {line}. Run them again for the other lines.',

  // Transaction labels
  txAddKey: 'Add keyframe',
  txAddKeys: 'Add keyframes',
  txMoveKeys: 'Move {count, plural, one {# keyframe} other {# keyframes}}',
  txDeleteKeys: 'Delete {count, plural, one {# keyframe} other {# keyframes}}',
  txHold: 'Stay, then jump',
  txMakeJump: 'Make jump',
  txContinue: 'Continue from previous',
  txNegate: 'Negate values',
  txOpacityFull: 'Opacity 100 %',
  txEase: 'Change curve',
  txLayerAdd: 'Add layer',
  txLayerDelete: 'Delete layer',
  txLayerRename: 'Rename layer',
  txLayerMove: 'Move to layer {n}',
  txLink: 'Link Position X and Y',
  txUnlink: 'Unlink Position X and Y',
  txReset: 'Reset {prop}',
  txAddProperty: 'Add {prop}',
  txPlaceSegment: 'Place segment',
  txFineTune: 'Fine-tune values',
  txNudge: 'Nudge keyframes',
  txSplit: 'Split at the playhead',
  txPasteCurve: 'Paste curve',

  // Help
  helpColumnsTitle: 'Motion columns',
  helpColumnsBody:
    'Each motion column shows how one property of the active line changes over time: time runs up, the value runs across. Click a column to focus it, then press K to add a keyframe at the playhead. Double-click inside a column to add a keyframe without changing the motion. Drag keyframes to change their time and value (Shift locks one axis, Alt ignores the snap, Ctrl rounds values). Select the curve between two keyframes and press Enter or C to change how it moves, H for "Stay, then jump", J to make a jump.',
  helpLayersTitle: 'Layers',
  helpLayersBody:
    'Position, Rotation, Opacity and Scroll speed can add up several layers (RPE event layers). The chips above a column choose the layer you edit; the dashed curve shows the sum. Mute and solo change only the preview: the export keeps every layer, and a banner says the preview differs.',
  helpLinkTitle: 'Position X and Y link',
  helpLinkBody:
    'When Position X and Y are linked, adding, moving, deleting, splitting or changing the curve of a keyframe on one axis does the same on the other at the same beat. The values stay independent. New lines start linked.',
  helpCompareTitle: 'Compare lines',
  helpCompareBody:
    'Pin column keeps a column next to the active line. Compare lines pins one property of every selected line side by side, so you can drag keyframes of several lines at once and apply curves to all of them.',
});

export default catalogOf(m);
